/**
 * medite-cart-drawer.js
 * Handles: upsell carousel · upsell add-to-cart · subscription upgrade · checkout prefetch · remove items · close button
 *
 * All interactive buttons use EVENT DELEGATION on the drawer element rather than
 * per-button binding. This means:
 *   - No need to rebind after content re-renders (new buttons just work automatically)
 *   - No double-binding race conditions between multiple initAll() calls
 *   - Listeners are set up once and flagged on the drawer with data-mcd-*-delegated
 */

(function () {
  'use strict';

  var _openTimer     = null;
  var _refreshing    = false;   /* guard: only one refreshDrawer() in flight at a time */
  var _refreshStart  = 0;       /* watchdog: Date.now() when _refreshing was last set   */
  var _giftsCheckStart = 0;     /* watchdog: Date.now() when ensureGifts last locked   */

  /* ─── Gift constants ──────────────────────────────────────────── */
  /*
   * any paid item : 1× Mystery Bottle (free, via the reactivated "Free Gift With Any Item"
   *                 100%-off discount — it makes the mystery bottle $0 on any order)
   * Empty cart    : 0× Mystery Bottle (removed)
   * 3-Pack in cart: 0× Mystery Bottle (gated — see the has3Pack note below: the 100%-off
   *                 discount does not stack with "Buy a 3-Pack, Get 2 Free", so a mystery
   *                 alongside a 3-Pack would be charged; the 3-Pack has its own 2 free bottles)
   *
   * ensureGifts() uses /cart/add.js for the initial add and /cart/update.js
   * to adjust quantity (or 0 to remove).
   */
  var MYSTERY_VARIANT_ID = 42845441884241;  /* mystery-bottle variant */
  var GIFT_THRESHOLD     = 0;               /* cents - $0.00: free Mystery Bottle with ANY order
                                               (the "Free Gift With Any Item" 100%-off discount is
                                               active again, so it's $0 at checkout on every order). */
  var _giftsChecking      = false;
  var _giftsPending       = false;   /* set when a cart change arrives while checking */
  var _giftsDebounceTimer = null;    /* debounce timer for event-driven gift checks */

  /* Debounced ensureGifts - use this for event listeners (qty changes, cart:refresh).
     Rapid +/- clicks collapse into a single call 200ms after the last click.
     Direct product adds (fetch interceptor) still call ensureGifts() immediately
     for fastest response when opening the drawer.                                */
  function scheduleEnsureGifts() {
    clearTimeout(_giftsDebounceTimer);
    _giftsDebounceTimer = setTimeout(ensureGifts, 200);
  }

  /* Returns true when the fetch is the initial /cart/add.js call from
     ensureGifts() (first-time mystery bottle add). Quantity updates go through
     /cart/change.js which is not intercepted at all, so no guard is needed
     for those. The X-Medite-Gift:1 header is set only by our own code. */
  function isGiftVariantAdd(init) {
    if (!init) return false;
    var h = init.headers;
    if (!h) return false;
    var hv = (typeof h.get === 'function') ? h.get('X-Medite-Gift') : h['X-Medite-Gift'];
    return hv === '1';
  }

  function scheduleDrawerOpen() {
    clearTimeout(_openTimer);
    _openTimer = setTimeout(function () {
      var drawer = document.getElementById('Cart-Drawer');
      if (!drawer) return;

      var alreadyOpen = drawer.classList.contains('active');

      /* Speed: open the drawer immediately so the user sees it at once,
         then refresh content in the background. The drawer shows a subtle
         loading indicator while the sections API call completes.
         Exception: if gifts are still being resolved, wait for them first
         (max 1 s) to avoid a visible item-count jump.                      */
      function openNow() {
        if (!alreadyOpen) {
          drawer.classList.add('active');
          drawer.classList.add('mcd-loading');
          document.body.classList.add('open-cc');
          document.body.classList.add('open-cart');
          document.dispatchEvent(new CustomEvent('cart-drawer:open'));
        }
        refreshDrawer(function () {
          drawer.classList.remove('mcd-loading');
        });
      }

      if (!_giftsChecking) {
        openNow();
      } else {
        var waited = 0;
        var poll = setInterval(function () {
          waited += 50;
          if (!_giftsChecking || waited >= 1000) {
            clearInterval(poll);
            openNow();
          }
        }, 50);
      }

    }, 50);  /* 50 ms debounce - just enough to collapse double-fires; drawer opens immediately */
  }

  /* ─── Auto gift tier management ─────────────────────────────── */
  /*
   * Manages mystery-bottle quantity as a function of cart state:
   *   Any paid item present (not a 3-Pack): qty 1 (free via the any-order 100%-off discount)
   *   3-Pack present                       : qty 0 (gated - non-stacking discount)
   *   Empty cart                           : qty 0 (removed)
   *
   * Spending = sum of final_line_price for non-mystery, non-gift items.
   * First add uses /cart/add.js; qty changes use /cart/change.js.
   * Mutex + pending-flag pattern prevents concurrent or recursive runs.
   * Any legacy _medite_gift-tagged items (e.g. old Invictus gift lines) are
   * cleaned up automatically on the first run after the update.
   */
  function ensureGifts() {
    if (_giftsChecking) { _giftsPending = true; return; }
    _giftsChecking   = true;
    _giftsCheckStart = Date.now();  /* watchdog timestamp */
    _giftsPending    = false;

    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {

        var mysteryItems = [];    /* all mystery-bottle lines found in cart           */
        var garbageGifts = [];    /* legacy _medite_gift items (non-mystery) - remove */
        var bonusItems   = [];    /* 5-Month free bottles (_medite_bonus) - managed   */
        var has3Pack     = false; /* Invictus 3-Pack present (the bonus trigger)      */
        var hasRealItem  = false;
        var hasSubscription = false; /* a paid line on a selling plan (subscription) is present */

        cart.items.forEach(function (item) {
          var isMystery = item.variant_id === MYSTERY_VARIANT_ID;
          var isOurGift = !!(item.properties && item.properties['_medite_gift'] === 'yes');
          var isBonus   = !!(item.properties && item.properties['_medite_bonus'] === '5month');

          if (item.variant_id === 42095201681489) has3Pack = true; /* Invictus 3-Pack */
          if (item.variant_id === 62807453827153) has3Pack = true; /* Invictus 2-Pack (Buy 2, Get 1 Free) - same bonus-bottle + gift rules */

          if (isBonus) {
            bonusItems.push(item);          /* 5-Month free bottles - never counted as paid   */
          } else if (isMystery) {
            mysteryItems.push(item);        /* collect ALL mystery lines - deduplicate below */
          } else if (isOurGift) {
            garbageGifts.push(item);        /* non-mystery gift tag - remove (legacy)        */
          } else {
            hasRealItem = true;             /* all paid items land here, never touched        */
            if (item.selling_plan_allocation) hasSubscription = true; /* subscription paid line */
          }
        });

        /* 5-Month bonus bottles are valid ONLY while a 3-Pack is in the cart. If the
           3-Pack is gone, remove the orphaned bonus - safety net behind the PDP
           toggle's own reconcile so stale free items can never linger. */
        var orphanBonus = has3Pack ? [] : bonusItems;

        /* Spending: exclude mystery bottle and any gift-tagged items (they are $0 anyway) */
        var spendingTotal = 0;
        cart.items.forEach(function (item) {
          var isMystery = item.variant_id === MYSTERY_VARIANT_ID;
          var isOurGift = !!(item.properties && item.properties['_medite_gift'] === 'yes');
          var isBonus   = !!(item.properties && item.properties['_medite_bonus'] === '5month');
          if (!isMystery && !isOurGift && !isBonus) spendingTotal += item.final_line_price;
        });

        /* Mystery Bottle is now "Free Gift With Any SUBSCRIPTION" (the 100%-off automatic
           discount was changed to apply only to subscription orders). So it is added ONLY
           when the cart contains a subscription line (a product subscribed directly, or the
           "upgrade & save" button that re-adds the cart on a selling plan). Adding it to a
           non-subscription order would NOT be covered by the discount and would charge the
           customer $18 for it. Idempotent: a recompute just converges on the target qty. */
        var GIFT_TIERS = [
          { threshold: GIFT_THRESHOLD, mystery: 1 }
        ];
        var targetQty = 0;
        if (hasRealItem && hasSubscription) {
          for (var ti = 0; ti < GIFT_TIERS.length; ti++) {
            if (spendingTotal >= GIFT_TIERS[ti].threshold) targetQty = GIFT_TIERS[ti].mystery;
          }
        }
        /* 3-Pack path: gated to 0. VERIFIED LIVE that the "Free Gift With Any Item"
           100%-off discount does NOT stack with the "Buy a 3-Pack, Get 2 Free" discount,
           so a Mystery Bottle added alongside a 3-Pack is charged full price ($17.99),
           overcharging the customer. The 3-Pack already grants its 2 free bonus bottles.
           Enabling the Mystery on this path requires the two automatic discounts to be set
           to "combine" in admin (Discounts settings), which is out of theme scope. Until
           then, keep it off here so the 3-Pack is never overcharged. */
        if (has3Pack) targetQty = 0;
        /* current mystery qty = SUM across any lines Shopify may have split */
        var currentQty = 0;
        mysteryItems.forEach(function (m) { currentQty += m.quantity; });

        /* Idempotent no-op: already correct and nothing to clean up. Resolve false so
           the completion below skips a redundant drawer re-render (the mutating action
           that triggered this check already rendered). This removes the extra section
           render + the late reflow on paths where gifts need no change (e.g. 3-Pack). */
        if (currentQty === targetQty && !garbageGifts.length && !orphanBonus.length) {
          return Promise.resolve(false);
        }

        var p = Promise.resolve();

        /* ── 1. Remove legacy gift-tagged items + orphaned 5-Month bonus bottles ── */
        if (garbageGifts.length || orphanBonus.length) {
          var cleanup = {};
          garbageGifts.forEach(function (g) { cleanup[g.key] = 0; });
          orphanBonus.forEach(function (b) { cleanup[b.key] = 0; });
          p = p.then(function () {
            return fetch('/cart/update.js', {
              method:  'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body:    JSON.stringify({ updates: cleanup })
            });
          });
        }

        /* ── 2. Set the Mystery Bottle to the target qty. Never /cart/change a 100%-off
           line (Shopify splits it, which is what destroyed the gift when crossing $75):
           zero every mystery line, then add the target once. ── */
        if (currentQty !== targetQty) {
          p = p.then(function () {
            if (!mysteryItems.length) return null;
            var mupd = {};
            mysteryItems.forEach(function (m) { mupd[m.key] = 0; });
            return fetch('/cart/update.js', {
              method:  'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body:    JSON.stringify({ updates: mupd })
            });
          }).then(function () {
            if (targetQty > 0) {
              return fetch('/cart/add.js', {
                method:  'POST',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Medite-Gift': '1' },
                body:    JSON.stringify({ id: MYSTERY_VARIANT_ID, quantity: targetQty, properties: { '_medite_gift': 'yes' } })
              });
            }
          });
        }

        return p.then(function () { return true; });   /* mutated -> a re-render is warranted */
      })
      .then(function (mutated) {
        _giftsChecking = false;
        if (_giftsPending) {
          _giftsPending = false;
          ensureGifts();
        } else if (mutated) {
          var drawer = document.getElementById('Cart-Drawer');
          if (drawer && drawer.classList.contains('active')) { setTimeout(refreshDrawer, 150); }
        }
      })
      .catch(function (err) {
        console.error('[MCD] ensureGifts error:', err);
        _giftsChecking = false;
        _giftsPending  = false;
      });
  }

  /* ─── Universal Cart-Open: intercept ALL /cart/add fetches ────── */
  /* Catches native theme adds AND third-party subscription app adds.
     Uses a 600ms debounce so concurrent adds (item + auto-gift) all
     land before we refresh + open. */
  (function () {
    var _origFetch = window.fetch;
    window.fetch = function (resource, init) {
      var url = typeof resource === 'string' ? resource
              : (resource && typeof resource.url === 'string' ? resource.url : '');
      var isCartAdd = url.indexOf('/cart/add') !== -1;

      var promise = _origFetch.apply(this, arguments);

      if (isCartAdd) {
        promise.then(function (response) {
          /* Dual-check: response.ok works when we get a real Response object.
             When UpCart (or any other fetch wrapper) pre-parses the body and
             resolves the promise with JSON instead of a Response, response.ok
             is undefined - fall back to Shopify's JSON error format where
             success responses have no `status` field and errors have status:422. */
          var succeeded = response != null && (
            typeof response.ok === 'boolean' ? response.ok : !response.status
          );
          if (succeeded) {
            /* Gift-variant adds must NOT reset the drawer timer or recurse.
               Only real-product adds (user intent) schedule the drawer. */
            if (!isGiftVariantAdd(init)) {
              /* Immediate run: drawer must open with gift already present.
                 The mutex+pending pattern catches any cart changes that
                 land while this run is in flight - no extra setTimeout needed. */
              ensureGifts();
              scheduleDrawerOpen();
            }
          }
        }).catch(function () {});
      }

      return promise;
    };
  }());

  /* ─── Upsell Carousel ─────────────────────────── */
  function initCarousel(drawer) {
    var track = drawer.querySelector('#mcd-upsell-track');
    var prevBtn = drawer.querySelector('.mcd-carousel-prev');
    var nextBtn = drawer.querySelector('.mcd-carousel-next');
    if (!track || !prevBtn || !nextBtn) return;

    function getItemWidth() {
      var item = track.querySelector('.mcd-upsell-item');
      if (!item) return 0;
      return item.offsetWidth + 10;
    }

    function updateButtons() {
      var maxScroll = track.scrollWidth - track.clientWidth;
      prevBtn.disabled = track.scrollLeft <= 2;
      nextBtn.disabled = track.scrollLeft >= maxScroll - 2;
    }

    prevBtn.addEventListener('click', function () {
      track.scrollBy({ left: -getItemWidth(), behavior: 'smooth' });
    });
    nextBtn.addEventListener('click', function () {
      track.scrollBy({ left: getItemWidth(), behavior: 'smooth' });
    });
    track.addEventListener('scroll', updateButtons, { passive: true });
    updateButtons();
  }

  /* ─── Upsell Add to Cart (delegated) ──────────── */
  /* One listener on the drawer - survives HTML re-renders automatically */
  function initUpsellButtons(drawer) {
    if (drawer.dataset.mcdUpsellDelegated) return;
    drawer.dataset.mcdUpsellDelegated = '1';

    drawer.addEventListener('click', function (e) {
      var btn = e.target.closest('.mcd-upsell-add-btn');
      if (!btn || btn.disabled) return;

      var variantId = parseInt(btn.dataset.variantId, 10);
      if (!variantId) return;

      var originalText = btn.textContent.trim();
      btn.textContent = 'Adding\u2026';
      btn.disabled = true;

      /* Build payload \u2014 include selling_plan only if the button carries one */
      var addPayload = { id: variantId, quantity: 1 };
      var planId = parseInt(btn.dataset.sellingPlan, 10);
      if (planId) addPayload.selling_plan = planId;

      fetch(window.theme ? window.theme.routes.cart_add_url : '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(addPayload)
      })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (data.status && data.status !== 200) {
            btn.textContent = data.description || 'Error';
            setTimeout(function () { btn.textContent = originalText; btn.disabled = false; }, 2500);
            return;
          }
          btn.textContent = 'Added \u2713';
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          setTimeout(function () { btn.textContent = originalText; btn.disabled = false; }, 2000);
        })
        .catch(function () {
          btn.textContent = 'Error';
          setTimeout(function () { btn.textContent = originalText; btn.disabled = false; }, 2500);
        });
    });
  }

  /* ─── Subscription Upgrade Button (delegated) ─── */
  function initUpgradeButton(drawer) {
    if (drawer.dataset.mcdUpgradeDelegated) return;
    drawer.dataset.mcdUpgradeDelegated = '1';

    drawer.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-upgrade]');
      if (!btn) return;
      e.preventDefault();

      var originalText = btn.textContent.trim();
      btn.textContent = 'Upgrading\u2026';
      btn.style.pointerEvents = 'none';

      /* Find best selling plan: prefer "every 4 weeks", fall back to first available */
      function findPlan(groups) {
        var fallback = null;
        for (var g = 0; g < groups.length; g++) {
          var plans = groups[g].selling_plans || [];
          for (var p = 0; p < plans.length; p++) {
            if (!fallback) fallback = plans[p].id;
            var n = (plans[p].name || '').toLowerCase();
            if (n.indexOf('4 week') >= 0 || n.indexOf('every 4') >= 0 || n.indexOf('28 day') >= 0) {
              return plans[p].id;
            }
          }
        }
        return fallback;
      }

      function reset() {
        btn.textContent = originalText;
        btn.style.pointerEvents = '';
      }

      function fail(msg) {
        btn.textContent = msg || 'Try again';
        setTimeout(reset, 2500);
      }

      /* Fast path: Liquid pre-baked plan map - skips all product.js fetches */
      var planMapRaw = btn.dataset.planMap || '';
      if (planMapRaw) {
        var upgFast = [];
        planMapRaw.split('|').forEach(function (entry) {
          if (!entry) return;
          var parts = entry.split('~');
          if (parts.length < 4) return;
          upgFast.push({ key: parts[0], variantId: parseInt(parts[1], 10), quantity: parseInt(parts[2], 10), planId: parseInt(parts[3], 10) });
        });
        if (upgFast.length) {
          var updFast = {};
          upgFast.forEach(function (u) { updFast[u.key] = 0; });
          fetch('/cart/update.js', {
            method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ updates: updFast })
          }).then(function () {
            return fetch('/cart/add.js', {
              method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ items: upgFast.map(function (u) { return { id: u.variantId, quantity: u.quantity, selling_plan: u.planId }; }) })
            });
          }).then(function () {
            document.dispatchEvent(new CustomEvent('cart:refresh'));
            btn.textContent = 'Upgraded \u2713';
            setTimeout(reset, 2000);
          }).catch(function (err) { console.error('Upgrade error:', err); fail('Error - try again'); });
          return;
        }
        /* upgFast empty (format mismatch) - fall through to slow path */
      }

      /* Slow-path fallback: fetch cart + product.js (runs when plan map absent) */
      fetch('/cart.js')
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var oneTimeItems = cart.items.filter(function (item) {
            return !item.selling_plan_allocation
              && item.final_price > 0
              && item.handle !== 'mystery-bottle'
              && !(item.properties && item.properties['_medite_gift'] === 'yes')
              /* Skip items already carrying a line-level discount (e.g. the dermaroller's
                 20% off): upgrading them to a subscription drops the discount and raises
                 the price, so leave them exactly as they are. */
              && !(item.line_level_discount_allocations && item.line_level_discount_allocations.length > 0);
          });
          if (!oneTimeItems.length) { fail('Already subscribed \u2713'); throw new Error('done'); }

          return Promise.all(oneTimeItems.map(function (item) {
            return fetch('/products/' + item.handle + '.js')
              .then(function (r) { return r.json(); })
              .then(function (product) {
                return { item: item, planId: findPlan(product.selling_plan_groups || []) };
              })
              .catch(function () { return { item: item, planId: null }; });
          }));
        })
        .then(function (results) {
          var upgrades = results.filter(function (r) { return r.planId; });
          if (!upgrades.length) { fail('No plans found'); throw new Error('done'); }

          /* Step 3 - zero out one-time items */
          var updates = {};
          upgrades.forEach(function (u) { updates[u.item.key] = 0; });

          return fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ updates: updates })
          }).then(function () {
            /* Step 4 - re-add as subscriptions */
            var items = upgrades.map(function (u) {
              return { id: u.item.variant_id, quantity: u.item.quantity, selling_plan: u.planId };
            });
            return fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ items: items })
            });
          });
        })
        .then(function () {
          /* Gift-tagged items (_medite_gift) and mystery-bottle were excluded
             from oneTimeItems in Step 1 - they were never zeroed out and remain
             in the cart. Re-adding them here caused the "extra gift" bug. */
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          btn.textContent = 'Upgraded \u2713';
          setTimeout(reset, 2000);
        })
        .catch(function (err) {
          if (!err || err.message !== 'done') {
            console.error('Upgrade error:', err);
            fail('Error \u2014 try again');
          }
        });
    });
  }

  /* ─── Robust drawer refresh ──────────────────────────────────
     Always call this instead of drawer.refresh() directly.
     Tries the theme's native method first; if unavailable falls
     back to the Shopify sections API so the drawer always shows
     the current cart state regardless of theme/app version.     */
  function refreshDrawer(onDone) {
    /* Skip redundant background refreshes; never skip when caller needs the onDone callback */
    if (_refreshing && !onDone) return;
    _refreshing = true;
    _refreshStart = Date.now();

    var drawer = document.getElementById('Cart-Drawer');
    if (!drawer) { _refreshing = false; if (onDone) onDone(); return; }

    /* Always use sections API - drawer.refresh() is opaque and can hang silently.
       Also pull cart-bubble so the header cart-count stays accurate on every cart
       change (delete/qty/gift sync all pass through here). */
    fetch(window.location.pathname + '?sections=cart-drawer,cart-bubble')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        updateHeaderCount(data && data['cart-bubble']);
        var html = data && data['cart-drawer'];
        if (!html) { _refreshing = false; if (onDone) onDone(); return; }
        var tmp = document.createElement('div');
        tmp.innerHTML = html;
        var fresh = tmp.querySelector('#Cart-Drawer');
        if (!fresh) { _refreshing = false; if (onDone) onDone(); return; }
        var wasActive = drawer.classList.contains('active');
        drawer.innerHTML = fresh.innerHTML;
        if (wasActive) {
          drawer.classList.add('active');
          document.body.classList.add('open-cc');
          document.body.classList.add('open-cart');
        }
        animateProgressBars();
        setTimeout(function () { _refreshing = false; initAll(); if (onDone) onDone(); }, 50);
      })
      .catch(function () { _refreshing = false; if (onDone) onDone(); });
  }

  /* Sync the header cart-count bubble from a fresh cart-bubble section render.
     Isolated + defensive: never throws, never touches anything but .thb-item-count. */
  function updateHeaderCount(bubbleHtml) {
    if (!bubbleHtml) return;
    try {
      var tmp = document.createElement('div');
      tmp.innerHTML = bubbleHtml;
      var fresh = tmp.querySelector('.thb-item-count');
      if (!fresh) return;
      document.querySelectorAll('#cart-drawer-toggle .thb-item-count, .thb-secondary-cart .thb-item-count').forEach(function (el) {
        el.innerHTML = fresh.innerHTML;
      });
    } catch (e) {}
  }

  /* ─── Checkout Prefetch ───────────────────────── */
  function prefetchCheckout() {
    if (document.querySelector('link[rel="prefetch"][href="/checkout"]')) return;
    var link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = '/checkout';
    document.head.appendChild(link);
  }

  /* ─── Checkout Early Navigation (pointerdown) ── */
  /* Fires ~100ms before click - shaves real latency off checkout load time.
     Mouse only: skip touch to avoid accidental nav mid-scroll on mobile. */
  function initCheckoutBtn(drawer) {
    if (drawer.dataset.mcdCheckoutDelegated) return;
    drawer.dataset.mcdCheckoutDelegated = '1';
    drawer.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse') return;
      if (e.target.closest('.mcd-checkout-btn')) {
        window.location.href = '/checkout';
      }
    });
  }

  /* ─── Close Button (delegated) ──────────────────── */
  /* The theme's PanelClose.close_panel() method is missing in the live build,
     so clicks on the X do nothing. We handle it ourselves: directly remove
     the active class and sync body classes. */
  function initCloseButton(drawer) {
    if (drawer.dataset.mcdCloseDelegated) return;
    drawer.dataset.mcdCloseDelegated = '1';

    drawer.addEventListener('click', function (e) {
      if (!e.target.closest('.mcd-close-btn')) return;
      drawer.classList.remove('active');
      document.body.classList.remove('open-cc');
      document.body.classList.remove('open-cart');
    });
  }

  /* ─── Body-class sync (MutationObserver) ──────── */
  /* Ensures open-cc / open-cart are removed whenever the drawer's 'active'
     class is removed by ANY means (theme handler, overlay click, etc.)
     so the page never freezes with scroll-lock stuck. */
  function initBodyClassSync(drawer) {
    if (drawer.dataset.mcdBodySyncInit) return;
    drawer.dataset.mcdBodySyncInit = '1';

    new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        if (m.attributeName !== 'class') return;
        var wasActive = (m.oldValue || '').split(' ').indexOf('active') !== -1;
        var isActive  = drawer.classList.contains('active');
        if (wasActive && !isActive) {
          document.body.classList.remove('open-cc');
          document.body.classList.remove('open-cart');
        }
      });
    }).observe(drawer, { attributes: true, attributeOldValue: true });

    /* Safety valve: if drawer is closed by any mechanism (including UpCart internals)
       but body scroll-lock classes weren't cleaned up, force-clear them every 250ms. */
    setInterval(function () {
      var d = document.getElementById('Cart-Drawer');
      if (!d || !d.classList.contains('active')) {
        document.body.classList.remove('open-cc');
        document.body.classList.remove('open-cart');
      }
    }, 500);
  }

  /* ─── Outside-click to close (document-level, once) ─ */
  /* Two-phase approach prevents the click that OPENS the drawer from
     immediately closing it again:
     - Capture phase fires before any handler → record if drawer was closed
     - Bubble phase fires after all handlers → skip if click just opened it */
  var _outsideClickInit = false;
  function initOutsideClick() {
    if (_outsideClickInit) return;
    _outsideClickInit = true;

    var _clickOpenedDrawer = false;

    document.addEventListener('click', function (e) {
      var drawer = document.getElementById('Cart-Drawer');
      _clickOpenedDrawer = !!(drawer && !drawer.classList.contains('active'));
    }, true); /* capture - fires before theme handler adds 'active' */

    document.addEventListener('click', function (e) {
      if (_clickOpenedDrawer) return; /* this click just opened the drawer */
      var drawer = document.getElementById('Cart-Drawer');
      if (!drawer || !drawer.classList.contains('active')) return;
      if (!drawer.contains(e.target)) {
        drawer.classList.remove('active');
        document.body.classList.remove('open-cc');
        document.body.classList.remove('open-cart');
      }
    }, false); /* bubble - fires after all handlers including theme's */
  }

  /* ─── Quantity +/- (delegated, no optimistic UI) ───────────────
     Shopify's automatic-discount functions split a discounted line on ANY /cart/change
     (proven with a single isolated call), so we never call /cart/change here. On a
     quantity change we instead:
       1. /cart/update.js - zero EVERY non-gift cart line in this variant + selling-plan
          group (handles any lines Shopify has already split),
       2. /cart/add.js    - add the target quantity ONCE,
       3. re-render from the cart.
     No optimistic UI (the number only moves after the server responds); both buttons and
     the input are disabled while a request is in flight; rapid clicks debounce into ONE
     write carrying the final absolute quantity. The desired qty is accumulated in JS. */
  var _qtyIntended = {};   /* per group-id ("variantId|planId") desired absolute quantity */
  var _qtyTimer    = {};   /* per group-id debounce timer */
  var _qtyBusy     = {};   /* per group-id in-flight guard */

  function qtyWrap(groupId) {
    var drawer = document.getElementById('Cart-Drawer');
    if (!drawer) return null;
    var sel = (window.CSS && CSS.escape) ? CSS.escape(groupId) : groupId.replace(/"/g, '\\"');
    return drawer.querySelector('.mcd-qty-selector[data-group="' + sel + '"]');
  }
  function setQtyDisabled(wrap, disabled) {
    if (!wrap) return;
    wrap.querySelectorAll('.mcd-qty-btn').forEach(function (b) { b.disabled = disabled; });
    var input = wrap.querySelector('.mcd-qty-input');
    if (input) input.disabled = disabled;
  }

  function commitQty(groupId) {
    var qty = _qtyIntended[groupId];
    if (qty == null) return;
    var parts     = groupId.split('|');
    var variantId = parseInt(parts[0], 10);
    var planId    = parts[1] ? parseInt(parts[1], 10) : 0;
    if (!variantId) { delete _qtyIntended[groupId]; return; }

    _qtyBusy[groupId] = true;
    var wrap = qtyWrap(groupId);
    var item = wrap && wrap.closest('.mcd-item');
    if (item) item.classList.add('thb-loading');
    setQtyDisabled(wrap, true);

    /* Zero every non-gift line matching this variant + plan, then add the target once. */
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var updates = {};
        cart.items.forEach(function (it) {
          var isGift = it.variant_id === MYSTERY_VARIANT_ID
            || (it.properties && (it.properties['_medite_gift'] === 'yes' || it.properties['_medite_bonus'] === '5month'));
          if (isGift) return;
          var itPlan = it.selling_plan_allocation ? it.selling_plan_allocation.selling_plan.id : 0;
          if (it.variant_id === variantId && itPlan === planId) updates[it.key] = 0;
        });
        return fetch('/cart/update.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ updates: updates })
        });
      })
      .then(function () {
        var addBody = { id: variantId, quantity: qty };
        if (planId) addBody.selling_plan = planId;
        return fetch('/cart/add.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(addBody)
        });
      })
      .then(function () {
        delete _qtyIntended[groupId];
        _qtyBusy[groupId] = false;
        ensureGifts();       /* a qty change can cross the $75 tier */
        _refreshing = false;
        refreshDrawer();     /* re-render from the real cart, never optimistic */
      })
      .catch(function () {
        delete _qtyIntended[groupId];
        _qtyBusy[groupId] = false;
        _refreshing = false;
        refreshDrawer();     /* resync the displayed number to the real cart */
      });
  }

  function initQtyButtons(drawer) {
    if (drawer.dataset.mcdQtyDelegated) return;
    drawer.dataset.mcdQtyDelegated = '1';

    drawer.addEventListener('click', function (e) {
      var btn = e.target.closest('.mcd-qty-minus, .mcd-qty-plus');
      if (!btn || btn.disabled) return;
      e.preventDefault();

      var wrap = btn.closest('.mcd-qty-selector');
      if (!wrap) return;
      var groupId = wrap.dataset.group;
      if (!groupId || _qtyBusy[groupId]) return;   /* ignore clicks while this group's request is in flight */

      var input = wrap.querySelector('.mcd-qty-input');
      /* Seed the intended quantity from the server-truth summed qty on the first click of a burst. */
      if (_qtyIntended[groupId] == null) {
        _qtyIntended[groupId] = parseInt(wrap.dataset.qty, 10) || parseInt(input && input.value, 10) || 1;
      }
      var min = parseInt(input && input.getAttribute('min'), 10); if (isNaN(min) || min < 1) min = 1;
      var max = parseInt(input && input.getAttribute('max'), 10);   /* NaN when uncapped */
      var next = _qtyIntended[groupId] + (btn.classList.contains('mcd-qty-plus') ? 1 : -1);
      if (next < min) next = min;
      if (!isNaN(max) && next > max) next = max;
      _qtyIntended[groupId] = next;   /* accumulate in JS only - no optimistic DOM change */

      var it = btn.closest('.mcd-item');
      if (it) it.classList.add('thb-loading');   /* loading feedback, not an optimistic number */
      clearTimeout(_qtyTimer[groupId]);
      _qtyTimer[groupId] = setTimeout(function () { commitQty(groupId); }, 350);
    });
  }

  /* ─── Remove Items (delegated, capture phase) ──── */
  /* Always uses direct Shopify API - never delegates to drawer.updateQuantity()
     which is unreliable and has no error handling. Handles both the "last item"
     and "not last item" cases explicitly. */
  function initRemoveButtons(drawer) {
    if (drawer.dataset.mcdRemoveDelegated) return;
    drawer.dataset.mcdRemoveDelegated = '1';

    drawer.addEventListener('click', function (e) {
      var btn = e.target.closest('.mcd-remove[data-line-keys]');
      if (!btn) return;

      e.preventDefault();
      e.stopImmediatePropagation();

      var itemEl = btn.closest('.mcd-item');
      if (itemEl && itemEl.dataset.isGift === 'true') return;

      /* A grouped row can back several split lines - remove EVERY key in the group. */
      var groupKeys = (btn.dataset.lineKeys || '').split(',').filter(Boolean);
      if (!groupKeys.length) return;

      if (itemEl) itemEl.classList.add('thb-loading');

      var allPaid  = drawer.querySelectorAll('.mcd-item:not([data-is-gift="true"])');
      var isLastPaid = allPaid.length <= 1;

      var updates = {};
      groupKeys.forEach(function (k) { updates[k] = 0; });

      if (isLastPaid) {
        /* Last paid group - zero it plus ALL gift lines in one call (unchanged behaviour:
           removing the last paid item clears every gift). */
        drawer.querySelectorAll('.mcd-item[data-is-gift="true"]').forEach(function (el) {
          (el.dataset.lineKeys || '').split(',').filter(Boolean).forEach(function (k) { updates[k] = 0; });
        });
        fetch('/cart/update.js', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body:    JSON.stringify({ updates: updates })
        })
          .then(function () { _refreshing = false; refreshDrawer(); })
          .catch(function () { if (itemEl) itemEl.classList.remove('thb-loading'); });

      } else {
        /* Not the last group - zero every line in the group, then re-render */
        fetch('/cart/update.js', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body:    JSON.stringify({ updates: updates })
        })
          .then(function (r) { if (!r.ok) throw new Error('update failed'); return r.json(); })
          .then(function () {
            _refreshing = false; /* clear mutex so refresh goes through immediately */
            ensureGifts();
            refreshDrawer();
          })
          .catch(function () { if (itemEl) itemEl.classList.remove('thb-loading'); });
      }

    }, true);
  }

  /* ─── Milestone Bar Animation ────────────────── */
  /* Liquid renders the target width server-side (style="width:X%").
     When the drawer opens or the cart refreshes, the DOM is either freshly
     created or replaced - so the CSS transition never fires on its own.
     This function reads each fill's target width, snaps it to 0, then
     restores it one double-rAF later so the browser paints the 0% frame
     first and plays the full transition to the target. */
  function animateProgressBars() {
    var drawer = document.getElementById('Cart-Drawer');
    if (!drawer) return;
    var fills = drawer.querySelectorAll('.mcd-ms-fill');
    if (!fills.length) return;

    fills.forEach(function (fill) {
      var target = fill.style.width || '0%';
      /* Snap to 0 without transition */
      fill.style.transition = 'none';
      fill.style.width = '0%';
      /* Double rAF: first frame paints the 0% state, second re-enables
         the CSS transition and sets the target - browser then animates. */
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          fill.style.transition = '';
          fill.style.width = target;
        });
      });
    });
  }

  /* ─── ESC key to force-close drawer ─────────── */
  /* Gives the user a guaranteed escape hatch if anything gets stuck. */
  function initEscKey() {
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      var drawer = document.getElementById('Cart-Drawer');
      if (!drawer || !drawer.classList.contains('active')) return;
      drawer.classList.remove('active');
      document.body.classList.remove('open-cc');
      document.body.classList.remove('open-cart');
    });
  }

  /* ─── Watchdog: thb-loading stuck on items ─── */
  /* CartDrawer.updateQuantity() in app.js has no .catch() - a network failure
     leaves the item with thb-loading (pointer-events:none) forever.
     Clear any item that's been in loading state for >4s. */
  (function () {
    var loadingTimestamps = new WeakMap();
    setInterval(function () {
      var drawer = document.getElementById('Cart-Drawer');
      if (!drawer) return;
      drawer.querySelectorAll('.thb-loading').forEach(function (el) {
        if (!loadingTimestamps.has(el)) {
          loadingTimestamps.set(el, Date.now());
        } else if (Date.now() - loadingTimestamps.get(el) > 4000) {
          el.classList.remove('thb-loading');
          loadingTimestamps.delete(el);
        }
      });
    }, 500);
  })();

  /* ─── Watchdog: _refreshing mutex stuck ─── */
  setInterval(function () {
    if (_refreshing && _refreshStart && (Date.now() - _refreshStart) > 3500) {
      _refreshing  = false;
      _refreshStart = 0;
    }
  }, 500);

  /* ─── Watchdog: _giftsChecking mutex stuck ─── */
  setInterval(function () {
    if (_giftsChecking && _giftsCheckStart && (Date.now() - _giftsCheckStart) > 3500) {
      _giftsChecking   = false;
      _giftsPending    = false;
      _giftsCheckStart = 0;
    }
  }, 500);

  /* ─── Watchdog: nuclear reset - if body scroll-lock has been on >10s with no open drawer ─── */
  setInterval(function () {
    var drawer = document.getElementById('Cart-Drawer');
    if (!drawer || !drawer.classList.contains('active')) {
      document.body.classList.remove('open-cc');
      document.body.classList.remove('open-cart');
    }
    /* Also reset mutexes if both are stuck simultaneously (shouldn't happen, but safety net) */
    if (_refreshing && _giftsChecking) {
      _refreshing = false; _refreshStart = 0;
      _giftsChecking = false; _giftsPending = false; _giftsCheckStart = 0;
    }
  }, 1000);

  /* ─── Cart toggle: ALWAYS open the drawer, never navigate to /cart ───
     The header cart icon is a plain <a href="/cart">. The drawer normally opens
     because a handler intercepts the click and calls preventDefault. If that click
     lands before the handler is attached (slow load) or the handler is ever missing,
     the raw link navigates to the full /cart page instead of opening the drawer.
     This capture-phase, document-level listener is registered the instant this script
     runs (it loads with defer, so #Cart-Drawer already exists) and owns the behavior:
     any click on the cart icon / a cart link opens the drawer and never falls through
     to /cart. If the drawer element is genuinely absent we let the link navigate as a
     last resort so the cart is always reachable. */
  function openDrawerNow() {
    var drawer = document.getElementById('Cart-Drawer');
    if (!drawer) return false;
    if (!drawer.classList.contains('active')) {
      drawer.classList.add('active');
      drawer.classList.add('mcd-loading');
      document.body.classList.add('open-cc');
      document.body.classList.add('open-cart');
      document.dispatchEvent(new CustomEvent('cart-drawer:open'));
      refreshDrawer(function () { drawer.classList.remove('mcd-loading'); });
    }
    return true;
  }
  document.addEventListener('click', function (e) {
    var toggle = e.target.closest(
      '#cart-drawer-toggle, .thb-secondary-cart, a[href="/cart"], a[href="/cart/"], a[href$="/cart"]'
    );
    if (!toggle) return;
    /* Modified clicks (new tab, etc.) keep their native behavior */
    if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    if (!document.getElementById('Cart-Drawer')) return;  /* no drawer -> allow /cart nav */
    e.preventDefault();
    e.stopPropagation();
    openDrawerNow();
  }, true);  /* capture - beats the default navigation and any theme handler */

  /* ─── Main Init ───────────────────────────────── */
  function initAll() {
    var drawer = document.getElementById('Cart-Drawer');
    if (!drawer) return;
    initCarousel(drawer);
    initUpsellButtons(drawer);
    initUpgradeButton(drawer);
    initQtyButtons(drawer);
    initRemoveButtons(drawer);
    initCheckoutBtn(drawer);
    initCloseButton(drawer);
    initBodyClassSync(drawer);
    initOutsideClick();
    initEscKey();
  }

  /* Run after drawer opens */
  document.addEventListener('cart-drawer:open', function () {
    prefetchCheckout();
    setTimeout(initAll, 50);
    /* Animate bars after content is visible */
    setTimeout(animateProgressBars, 200);
  });

  /* Re-init after cart item changes (content is re-rendered) */
  document.addEventListener('line-item:change:end', function () {
    setTimeout(initAll, 150);
    /* Quantity changes can shift the $75 threshold - debounce so rapid
       +/- clicks collapse to a single gift-logic evaluation. */
    scheduleEnsureGifts();
  });

  /* Open drawer whenever any item is added to cart (product pages, quick-add, etc.)
     Do NOT open immediately - let scheduleDrawerOpen() handle it after refresh
     so the drawer always opens showing the cart WITH gifts already present (no flash). */
  document.addEventListener('cart:item-added', function () {
    ensureGifts();        /* mutex: no-op if interceptor already running */
    scheduleDrawerOpen(); /* waits for gifts, refreshes, THEN opens drawer */
  });

  /* Re-init after cart content refresh (qty changes, removes, upsell adds) */
  document.addEventListener('cart:refresh', function () {
    setTimeout(initAll, 100);
    /* Re-animate bars after re-render settles */
    setTimeout(animateProgressBars, 300);
    /* Cart state changed - debounced gift check so rapid events collapse */
    scheduleEnsureGifts();
  });

  /* Initial run on page load */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  /* Silently ensure gifts on every page load - catches carts that were
     populated in a prior session without mystery bottle or Invictus.
     Runs after the page is idle so it never competes with initial render. */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { setTimeout(scheduleEnsureGifts, 1200); });
  } else {
    setTimeout(scheduleEnsureGifts, 1200);
  }

  /* Pre-warm /checkout during the browser's first idle window after load.
     requestIdleCallback fires when the main thread is free, so this never
     competes with parsing, layout, or user interactions.
     The {timeout: 3000} guarantee means it runs within 3s even if the browser
     stays busy (e.g. slow devices). Falls back to setTimeout on Safari < 17. */
  if ('requestIdleCallback' in window) {
    requestIdleCallback(prefetchCheckout, { timeout: 3000 });
  } else {
    setTimeout(prefetchCheckout, 2000);
  }

})();
